/**
 * What a scorecard shows: one row of its view (a category or a measure) in
 * one column, the change against the column before or against a target,
 * whether that change is good, and the column's rank among the periods.
 * Pure.
 */

import type { AxisLeaf, NumberFormatSpec, TableModel } from "utils/tableModel"
import { formatCell } from "utils/tableModel"
import type {
  RankMeasure,
  ScorecardTile,
} from "features/Dashboard/types/dashboardDocument"
import { leafLabel } from "./modelAdapters"
import { heldStatistic, measureMap } from "./measures"

export interface ScorecardValues {
  value: number | null
  /** The value in the row's format (`decimals` replacing its places). */
  text: string
  /** The row's label. */
  label: string
  /** The column's id and label. */
  columnId: string
  columnLabel: string
  /** Whether the column is a Totals column. */
  columnIsTotal: boolean
  /** Current minus previous, or current minus target; points for a percentage. */
  delta: number | null
  /** "+5.0 pts" for a percentage row, "+12" or "-0.25" otherwise; empty without a delta. */
  deltaText: string
  /** What the delta is measured against: the previous column's label, or "target 20". */
  compareLabel: string
  /** Whether the change goes the good way; null without a change. */
  good: boolean | null
  /** The column's rank among the periods, 1 = best; null when not asked or not ranked. */
  rank: { position: number; of: number } | null
  /** The leaves of the value's cell. */
  down: AxisLeaf
  across: AxisLeaf
}

const RANK_ID = "__scorecard_rank"

const numeric = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null

/** `n` with its English ordinal suffix: 1st, 2nd, 3rd, 4th, 11th, 22nd. */
export const ordinal = (n: number): string => {
  const tens = n % 100
  if (tens >= 11 && tens <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

/**
 * `delta` as a signed number in `places` decimals: "+5.0", "-13.4", and no
 * sign when it rounds to zero. A percentage row's delta is in points.
 */
const signed = (
  delta: number,
  places: number,
  thousands: boolean,
  points: boolean
): { text: string; zero: boolean } => {
  const body = formatCell({
    raw: Math.abs(delta),
    stat: "",
    kind: "count",
    format: { decimals: places, thousands, percent: false },
  })
  const zero = Number(body.replace(/,/g, "")) === 0
  const sign = zero ? "" : delta > 0 ? "+" : "-"
  return { text: `${sign}${body}${points ? " pts" : ""}`, zero }
}

/**
 * The scorecard of `view` (the tile's view) for `visual`, in `statistic`
 * (the tile's `viewStatistic`, so it reads its measure rows in the
 * statistic they were computed in; the first statistic the view holds
 * when it lacks that one):
 * - row: `visual.source` (a category or measure id), or the first row;
 * - column: `visual.column`, or the last non-Totals column, or Totals when
 *   the table has no column variable;
 * - delta: for "previous", this column minus the column before it (none
 *   for the first column or Totals); for "target", the value minus
 *   `target`, typed in the row's units (20 on a percentage row is 20%).
 *   A percentage row's delta is in points;
 * - good: the delta's direction against `goodDirection`;
 * - rank: with `showRank`, the rank of the column among the non-Totals
 *   columns by the rank measure's rule (highest first when up is good,
 *   ties sharing a place).
 * Null when the view has no such row or column.
 */
export const scorecardValues = (
  view: TableModel,
  visual: ScorecardTile["visual"],
  statistic: string
): ScorecardValues | null => {
  const down = view.down.leaves
  const across = view.across.leaves
  // The statistic asked for, or the first one the view holds, as the
  // measures fall back.
  const stat = heldStatistic(view, statistic)
  const holdsDown = (i: number): boolean =>
    view.statAxis !== "down" ||
    down[i].stat === undefined ||
    down[i].stat === stat
  const holdsAcross = (i: number): boolean =>
    view.statAxis !== "across" ||
    across[i].stat === undefined ||
    across[i].stat === stat
  const r = visual.source
    ? down.findIndex((l, i) => l.id === visual.source && holdsDown(i))
    : down.findIndex((l, i) => !l.isTotal && holdsDown(i))
  if (r < 0) return null
  const periods = across.flatMap((l, i) =>
    !l.isTotal && holdsAcross(i) ? [i] : []
  )
  const c =
    visual.column !== null
      ? across.findIndex((l, i) => l.id === visual.column && holdsAcross(i))
      : periods.length > 0
        ? periods[periods.length - 1]
        : across.findIndex((l, i) => l.isTotal && holdsAcross(i))
  if (c < 0) return null
  const cell = view.cells[r]?.[c]
  if (!cell) return null

  const own: NumberFormatSpec | null = cell.format
  const format: NumberFormatSpec | null =
    visual.decimals !== undefined
      ? {
          decimals: visual.decimals,
          thousands: own?.thousands ?? true,
          percent: own?.percent ?? false,
        }
      : own
  const value = numeric(cell.raw)
  const percent = format?.percent === true
  const places = format?.decimals ?? 0
  const thousands = !percent && (format?.thousands ?? false)

  let delta: number | null = null
  let compareLabel = ""
  if (visual.compare === "previous") {
    const at = periods.indexOf(c)
    const before = at > 0 ? periods[at - 1] : -1
    const previous = before >= 0 ? numeric(view.cells[r]?.[before]?.raw) : null
    if (value !== null && previous !== null) {
      delta = (value - previous) * (percent ? 100 : 1)
      compareLabel = leafLabel(across[before])
    }
  } else if (visual.compare === "target" && visual.target !== undefined) {
    if (value !== null) {
      delta = (percent ? value * 100 : value) - visual.target
      compareLabel = `target ${formatCell({
        raw: visual.target,
        stat: "",
        kind: "count",
        format: { decimals: places, thousands, percent: false },
      })}${percent ? "%" : ""}`
    }
  }
  const shown =
    delta === null ? null : signed(delta, places, thousands, percent)
  const good =
    delta === null || !shown || shown.zero
      ? null
      : visual.goodDirection === "up"
        ? delta > 0
        : delta < 0

  let rank: ScorecardValues["rank"] = null
  if (visual.showRank && periods.includes(c)) {
    const measure: RankMeasure = {
      id: RANK_ID,
      label: "",
      kind: "rank",
      source: down[r].id,
      direction: visual.goodDirection === "up" ? "desc" : "asc",
    }
    const ranks = measureMap(view, [measure], stat).get(RANK_ID) ?? []
    const position = ranks[c] ?? null
    if (position !== null) {
      rank = {
        position,
        of: periods.filter((p) => (ranks[p] ?? null) !== null).length,
      }
    }
  }

  return {
    value,
    text: formatCell({ ...cell, format }),
    label: leafLabel(down[r]),
    columnId: across[c].id,
    columnLabel: leafLabel(across[c]),
    columnIsTotal: across[c].isTotal,
    delta,
    deltaText: shown?.text ?? "",
    compareLabel,
    good,
    rank,
    down: down[r],
    across: across[c],
  }
}
